import { z } from 'zod';
import { timeFormatSchema } from './time.js';
import { SERVICE_NOTE_KINDS } from './noteKinds.js';
import { PAYOR_STATES } from './payors.js';
import { practiceNoteSettingsSchema } from './practiceNoteSettings.js';
import { peerActivitiesSchema, peerCredentialsSchema } from './peerSupport.js';
/**
 * Practice settings (Phase 20 / v1.1 Settings) — Locations + Company Holidays.
 * Shared so the SPA validates identically to the API (README §10).
 */
export const LOCATION_TYPES = ['Office', 'Remote', 'Satellite'];
export const locationCreateSchema = z.object({
    name: z.string().min(1).max(120),
    type: z.enum(LOCATION_TYPES).default('Office'),
    line1: z.string().max(200).default(''),
    line2: z.string().max(200).default(''),
    city: z.string().max(120).default(''),
    state: z.string().max(2).default(''), // 2-letter code
    zip: z.string().max(12).default(''), // ZIP or ZIP+4
    // ehr#409: the same checks as editing one (locationContactSchema): blank, or a 10-digit phone /
    // a real email. Adding a location only checked the length.
    phone: z
        .string()
        .trim()
        .max(40)
        .refine((v) => [0, 10].includes(v.replace(/\D/g, '').length), 'Use a 10-digit phone number')
        .default(''),
    email: z.union([z.literal(''), z.string().trim().email().max(200)]).default(''), // blank allowed — front-desk email is optional
    // ehr#638: the location's open hours (openHoursSchema), optional; none = not set yet.
    hours: z.lazy(() => openHoursSchema).optional(),
});
/**
 * ehr#302 - edit a location's phone and front-desk email (Settings → Locations). Each is
 * optional; '' clears it. A phone is a complete 10-digit number; an email must look like one.
 */
export const locationContactSchema = z
    .object({
    phone: z
        .string()
        .trim()
        .max(40)
        .refine((v) => [0, 10].includes(v.replace(/\D/g, '').length), 'Use a 10-digit phone number')
        .optional(),
    email: z.union([z.literal(''), z.string().trim().email().max(200)]).optional(),
})
    .refine((o) => o.phone !== undefined || o.email !== undefined, 'Nothing to update');
/**
 * ehr#638 (Chase 10-24) - a practice location's open hours, one row per day, Monday first. The same
 * shape as the signup wizard's Office hours (signup-credify: {day, open, start, end}), so the hours
 * a practice gives at signup can land here as they are. Times are 'HH:MM' 24-hour; a closed day
 * keeps '' (or the times it had, which are ignored). Settings → Practice info edits the primary
 * location's hours: they live on the location (locations.hours), never in a second copy.
 */
export const OPEN_HOURS_DAYS = [
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
    'Sunday',
];
const hhmm = /^([01][0-9]|2[0-3]):[0-5][0-9]$/;
/** What is wrong with a set of open hours, one line per problem; [] when it can be saved. */
export function openHoursErrors(hours) {
    const out = [];
    if (hours.length !== OPEN_HOURS_DAYS.length)
        return ['Give all seven days, Monday to Sunday.'];
    hours.forEach((h, i) => {
        if (h.day !== OPEN_HOURS_DAYS[i])
            out.push('Give the days in order, Monday to Sunday.');
        if (!h.open)
            return;
        if (!hhmm.test(h.start) || !hhmm.test(h.end))
            out.push(`${h.day}: pick when it opens and closes.`);
        else if (h.start >= h.end)
            out.push(`${h.day}: it has to close after it opens.`);
    });
    return [...new Set(out)];
}
export const openHoursSchema = z
    .array(z.object({
    day: z.enum(OPEN_HOURS_DAYS),
    open: z.boolean(),
    start: z.union([z.literal(''), z.string().regex(hhmm, 'Use HH:MM')]),
    end: z.union([z.literal(''), z.string().regex(hhmm, 'Use HH:MM')]),
}))
    .superRefine((hours, ctx) => {
    for (const message of openHoursErrors(hours))
        ctx.addIssue({ code: 'custom', message });
});
/** PUT /practice/locations/:id/hours. */
export const locationHoursSchema = z.object({ hours: openHoursSchema });
/** The hours a location starts from before any are saved: Monday to Friday, 9 to 5. */
export function defaultOpenHours() {
    return OPEN_HOURS_DAYS.map((day, i) => ({
        day,
        open: i < 5,
        start: i < 5 ? '09:00' : '',
        end: i < 5 ? '17:00' : '',
    }));
}
/** A stored value read back as open hours, or null when it isn't a valid set (never set, or old). */
export function readOpenHours(raw) {
    const r = openHoursSchema.safeParse(raw);
    return r.success ? r.data : null;
}
const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Date must be YYYY-MM-DD');
/**
 * ehr#376 - a company holiday is one of:
 *  - 'date': every year on that month and day ("January 1"); `date` keeps the date it was entered as.
 *  - 'rule': every year on the nth weekday of a month ("Fourth Thursday in November"); `date` holds
 *    its next date (as of saving) for readers that only know dates.
 *  - 'once': that one day only. Holidays saved before ehr#376 (they were entered with a year).
 * A rule can carry `ruleOffset` days after that weekday (Chase 10-13: "Day after Thanksgiving" is
 * the Friday after the fourth Thursday in November, which isn't always the fourth Friday).
 */
export const HOLIDAY_KINDS = ['date', 'rule', 'once'];
/** Occurrence: 1-4 = First..Fourth, 5 = Last. */
export const HOLIDAY_NTH_LABELS = ['First', 'Second', 'Third', 'Fourth', 'Last'];
export const WEEKDAY_NAMES = [
    'Sunday',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
];
export const MONTH_NAMES = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
];
export const holidayCreateSchema = z
    .object({
    // Without a kind it's a date, as before ehr#376.
    kind: z.enum(['date', 'rule']).default('date'),
    date: isoDate.optional(),
    ruleNth: z.number().int().min(1).max(5).optional(),
    ruleWeekday: z.number().int().min(0).max(6).optional(), // 0 = Sunday
    ruleMonth: z.number().int().min(1).max(12).optional(),
    name: z.string().trim().min(1).max(160),
    // ehr#376: one location, several ("Main Office, Brooklyn") or "All locations"
    observed: z.string().max(1000).default('All locations'),
})
    .superRefine((v, ctx) => {
    if (v.kind === 'date' && !v.date)
        ctx.addIssue({ code: 'custom', path: ['date'], message: 'Pick a date' });
    if (v.kind === 'rule')
        for (const k of ['ruleNth', 'ruleWeekday', 'ruleMonth'])
            if (v[k] === undefined)
                ctx.addIssue({
                    code: 'custom',
                    path: [k],
                    message: 'Pick the occurrence, weekday and month',
                });
});
const pad2 = (n) => String(n).padStart(2, '0');
const daysIn = (year, month) => new Date(Date.UTC(year, month, 0)).getUTCDate();
/** The day of the month of the nth (5 = last) weekday (0 = Sunday) of a month (1-12). */
export function nthWeekdayDay(year, month, weekday, nth) {
    if (nth >= 5) {
        const last = daysIn(year, month);
        const lastDow = new Date(Date.UTC(year, month - 1, last)).getUTCDay();
        return last - ((lastDow - weekday + 7) % 7);
    }
    const firstDow = new Date(Date.UTC(year, month - 1, 1)).getUTCDay();
    return 1 + ((weekday - firstDow + 7) % 7) + (nth - 1) * 7;
}
const isRule = (h) => h.kind === 'rule' && h.ruleNth != null && h.ruleWeekday != null && h.ruleMonth != null;
const kindOf = (h) => isRule(h) ? 'rule' : h.kind === 'date' ? 'date' : 'once';
/** The holiday's date ('YYYY-MM-DD') in a year, or null when it has none that year. */
export function holidayDateIn(h, year) {
    const kind = kindOf(h);
    if (kind === 'rule') {
        const m = h.ruleMonth;
        const d = new Date(Date.UTC(year, m - 1, nthWeekdayDay(year, m, h.ruleWeekday, h.ruleNth)));
        d.setUTCDate(d.getUTCDate() + (h.ruleOffset ?? 0));
        return d.toISOString().slice(0, 10);
    }
    if (kind === 'once')
        return h.date.slice(0, 4) === String(year) ? h.date : null;
    const mm = Number(h.date.slice(5, 7));
    const dd = Number(h.date.slice(8, 10));
    if (dd > daysIn(year, mm))
        return null; // February 29 outside a leap year
    return `${year}-${pad2(mm)}-${pad2(dd)}`;
}
/** Is the practice closed for this holiday on `ds` ('YYYY-MM-DD')? */
export function isHolidayOn(h, ds) {
    return holidayDateIn(h, Number(ds.slice(0, 4))) === ds;
}
/** Its next date on or after `fromDs` ('YYYY-MM-DD'), or null (a 'once' date already past). */
export function nextHolidayDate(h, fromDs) {
    const y = Number(fromDs.slice(0, 4));
    for (const year of [y, y + 1, y + 2, y + 3, y + 4]) {
        const d = holidayDateIn(h, year);
        if (d && d >= fromDs)
            return d;
    }
    return null;
}
/**
 * How the holiday reads: "January 1", "Third Monday in January", "Day after the Fourth Thursday in
 * November", or "July 3, 2026".
 */
export function holidayLabel(h) {
    const kind = kindOf(h);
    if (kind === 'rule') {
        const rule = `${HOLIDAY_NTH_LABELS[h.ruleNth - 1]} ${WEEKDAY_NAMES[h.ruleWeekday]} in ${MONTH_NAMES[h.ruleMonth - 1]}`;
        const off = h.ruleOffset ?? 0;
        if (!off)
            return rule;
        const days = Math.abs(off) === 1 ? 'Day' : `${Math.abs(off)} days`;
        return `${days} ${off > 0 ? 'after' : 'before'} the ${rule}`;
    }
    const mm = Number(h.date.slice(5, 7));
    const dd = Number(h.date.slice(8, 10));
    const md = `${MONTH_NAMES[mm - 1] ?? ''} ${dd}`;
    return kind === 'once' ? `${md}, ${h.date.slice(0, 4)}` : md;
}
export const STANDARD_HOLIDAYS = [
    { name: "New Year's Day", kind: 'date', monthDay: '01-01' },
    { name: 'Memorial Day', kind: 'rule', ruleNth: 5, ruleWeekday: 1, ruleMonth: 5 },
    { name: 'Independence Day', kind: 'date', monthDay: '07-04' },
    { name: 'Labor Day', kind: 'rule', ruleNth: 1, ruleWeekday: 1, ruleMonth: 9 },
    { name: 'Thanksgiving Day', kind: 'rule', ruleNth: 4, ruleWeekday: 4, ruleMonth: 11 },
    {
        name: 'Day after Thanksgiving',
        kind: 'rule',
        ruleNth: 4,
        ruleWeekday: 4,
        ruleMonth: 11,
        ruleOffset: 1,
    },
    { name: 'Christmas Eve', kind: 'date', monthDay: '12-24' },
    { name: 'Christmas Day', kind: 'date', monthDay: '12-25' },
    { name: "New Year's Eve", kind: 'date', monthDay: '12-31' },
];
/**
 * The standard holidays a practice is missing: none with the same name (any case), and no date
 * holiday already on the same month and day.
 */
export function missingStandardHolidays(existing) {
    const names = new Set(existing.map((h) => h.name.trim().toLowerCase()));
    const monthDays = new Set(existing.filter((h) => h.kind === 'date').map((h) => h.date.slice(5)));
    return STANDARD_HOLIDAYS.filter((s) => !names.has(s.name.toLowerCase()) && !(s.kind === 'date' && monthDays.has(s.monthDay)));
}
// ── Booking links (Email & calendar tab) ────────────────────────────────────
export const bookingLinkCreateSchema = z.object({
    name: z.string().min(1).max(160),
    minutes: z.number().int().min(5).max(240).default(30),
    url: z.string().url().max(400),
});
// ── Carrier accounts (Carrier accounts tab) ─────────────────────────────────
// The `secret` is write-only: sent only when the user enters a new value, and
// NEVER returned (reads expose only `hasSecret` + `secretLast4`).
const carrierInputSchema = z.object({
    phoneNumber: z.string().max(40).default(''),
    username: z.string().max(200).default(''),
    secret: z.string().max(400).optional(),
    connectionId: z.string().max(80).default(''),
    appSid: z.string().max(80).default(''),
});
const forwardingSchema = z.object({
    enabled: z.boolean().default(true),
    forwardTo: z.string().max(40).default(''),
    forwardAfter: z.string().max(40).default('5 rings'),
    contactFirst: z.string().max(80).default(''),
    contactLast: z.string().max(80).default(''),
    contactEmail: z.string().max(200).default(''),
});
export const carriersUpdateSchema = z.object({
    telnyx: carrierInputSchema.default({}),
    twilio: carrierInputSchema.default({}),
    forwarding: forwardingSchema.default({}),
});
// ── Clinical notes settings (Clinical notes tab, calendar#46 step 5) ────────
// Session-summary dictation uses the browser's speech recognition, which sends the
// audio to the browser vendor's speech service, so it stays off until the practice
// turns it on.
// ehr#437 (CASE_MANAGEMENT_NOTES_10-14-V25, V58 21 B): each Service note's default billing code
// (Case management, Peer support, Skills / rehab, Collateral contact). Not set: the built-in code
// (H0006, H0038, H2014, 90846); '' : no default, billed by hand. ehr#726: and Psych testing's.
/**
 * The codes the setting offers (V81 CPT list, service codes). ehr#725 (CASE_MANAGEMENT_NOTE V9
 * 14 A): T1016 (case management, each 15 min) too, for the case management Default billing code.
 */
export const SERVICE_CODE_CHOICES = [
    'H0006',
    'T1016',
    'H0004',
    'H0031',
    'H0038',
    'H2014',
    'H2017',
    'H2019',
    'T1017',
    '90846',
    '90887',
];
/**
 * ehr#726 (PSYCH_TESTING_NOTE_10-30-V4, 10a): the codes a Psych testing note's default may be. Not
 * set or '': No default (the V4 start), so the clinician picks the code by hand.
 */
export const PSYCH_TESTING_CODE_CHOICES = ['96130', '96136'];
/** The kinds the setting gives a default code: the Service notes, then Psych testing (ehr#726). */
export const DEFAULT_CODE_KINDS = [...SERVICE_NOTE_KINDS, 'Psych testing'];
const serviceCodesSchema = z
    .record(z.enum(DEFAULT_CODE_KINDS), z.union([z.enum(SERVICE_CODE_CHOICES), z.enum(PSYCH_TESTING_CODE_CHOICES), z.literal('')]))
    // each kind only from its own list: Psych testing 96130 / 96136, a Service note the service codes
    .superRefine((m, ctx) => {
    for (const [k, v] of Object.entries(m)) {
        if (!v)
            continue;
        const list = k === 'Psych testing' ? PSYCH_TESTING_CODE_CHOICES : SERVICE_CODE_CHOICES;
        if (!list.includes(v))
            ctx.addIssue({ code: 'custom', path: [k], message: `${v} is not a ${k} code.` });
    }
})
    .default({});
/**
 * ehr#437 (V81 Note settings → Interventions, admins): the practice's list for "Interventions used" on
 * every note. Not set: the built-in INTERVENTION_ITEMS. Notes keep what they recorded.
 */
const interventionListSchema = z
    .array(z.string().trim().min(1).max(60))
    .max(60)
    .refine((l) => new Set(l.map((x) => x.toLowerCase())).size === l.length, {
    message: 'Each intervention once.',
});
/**
 * ehr#682 (PROGRESS_NOTE_10-20-V141 V127): interventions hidden from "Interventions used". `all` is
 * the all-clients list; a client's own list (`clients[contactId]`) wins over it, as Quick add does.
 * Hidden only hides: a ticked one still shows, and search still finds it.
 */
const interventionNames = z.array(z.string().trim().min(1).max(60)).max(60);
export const interventionHideSchema = z.object({
    all: interventionNames.default([]),
    clients: z
        .record(z.string().min(1).max(64), interventionNames)
        .refine((c) => Object.keys(c).length <= 10000, { message: 'Too many client lists.' })
        .default({}),
});
/** ehr#571 (PROGRESS_NOTE_10-20-V141 V120): ask for a reason when a note is signed this many hours after the session. */
export const LATE_ENTRY_HOURS_DEFAULT = 72;
const lateEntryHoursSchema = z.number().int().min(1).max(720);
export const clinicalSettingsSchema = z.object({
    dictationEnabled: z.boolean().default(false),
    serviceCodes: serviceCodesSchema,
    interventions: interventionListSchema.optional(),
    interventionsHidden: interventionHideSchema.optional(),
    lateEntryHours: lateEntryHoursSchema.default(LATE_ENTRY_HOURS_DEFAULT),
    // ehr#682 (V141 Practice note settings): written by PUT /practice/note-settings
    ...practiceNoteSettingsSchema.shape,
    // ehr#727 (PEER_SUPPORT_NOTE V8): the Peer support activities and credentials lists, grown by
    // POST /practice/peer-lists (not set: the V8 lists, see readPeerLists)
    peerActivities: peerActivitiesSchema.optional().catch(undefined),
    peerCredentials: peerCredentialsSchema.optional().catch(undefined),
});
/** PUT /practice/clinical: only the settings sent change. */
export const clinicalSettingsInputSchema = z
    .object({
    dictationEnabled: z.boolean().optional(),
    serviceCodes: serviceCodesSchema.optional(),
    interventions: interventionListSchema.optional(),
    interventionsHidden: interventionHideSchema.optional(),
    lateEntryHours: lateEntryHoursSchema.optional(),
})
    .refine((v) => v.dictationEnabled !== undefined ||
    v.serviceCodes !== undefined ||
    v.interventions !== undefined ||
    v.interventionsHidden !== undefined ||
    v.lateEntryHours !== undefined, {
    message: 'Send a setting to change.',
});
// ── Notification settings (Notifications tab) ───────────────────────────────
export const notificationSettingsSchema = z.object({
    bell: z.boolean().default(true),
    email: z.boolean().default(false),
    push: z.boolean().default(false),
    bucketAlerts: z
        .object({
        enabled: z.boolean().default(true),
        staleAfter: z.string().max(40).default('24 hours'),
        userIds: z.array(z.string().max(80)).default([]),
    })
        .default({}),
    templates: z
        .array(z.object({
        name: z.string().min(1).max(160),
        subject: z.string().max(300).default(''),
        channel: z.string().max(40).default('Email'),
    }))
        .default([]),
});
// ── Forms Hub > Form Rules > Notifications & Workflow (alertshub#4) ─────────
// Which Forms Hub alerts the practice sends. Jay 2026-10-12: every switch starts ON (a trigger
// missing from triggerToggles is on), so the alerts that post today keep posting until someone
// turns one off. The server reads it (lib/forms-workflow.ts); the modal saves it.
export const FORMS_TRIGGERS = [
    'submitted',
    'incomplete',
    'signoff_complete',
    'signoff_overdue',
    'assigned',
    'completion_overdue',
];
/** "Opened but not completed": how long a started form sits before the sender hears. */
export const FORMS_DELAYS = ['24 hours', '48 hours', '3 days', '7 days'];
export const formsWorkflowSchema = z.object({
    triggerToggles: z.record(z.string().max(40), z.boolean()).default({}),
    delays: z.record(z.string().max(40), z.enum(FORMS_DELAYS)).default({}),
    signoffType: z.enum(['none', 'clinician', 'cosign', 'clinician_only']).default('none'),
    autoRules: z
        .array(z.object({
        id: z.string().min(1).max(40),
        on: z.boolean(),
        trigger: z.string().max(60),
        target: z.string().max(40),
    }))
        .max(50)
        .default([{ id: 'ar1', on: true, trigger: 'intake_started', target: 'portal' }]),
});
// ── Branding & labels (Branding tab) ────────────────────────────────────────
export const brandingSchema = z.object({
    displayName: z.string().max(120).default(''),
    labels: z.record(z.string(), z.string()).default({}), // { defaultTerm: overrideTerm }
});
// ── Practice logo (Practice info tab, fbs#99) ────────────────────────────────
// A data URL the browser downscaled (max 600 x 200), or '' to remove it. Same image
// types as Provider Profile photos; SVG is refused (it can carry script).
export const PRACTICE_LOGO_RE = /^data:image\/(png|jpeg|webp);base64,[A-Za-z0-9+/=]+$/;
export const PRACTICE_LOGO_MAX_CHARS = 500_000;
export const practiceLogoSchema = z.object({
    logo: z
        .string()
        .max(PRACTICE_LOGO_MAX_CHARS, 'The logo is too large. Use a smaller image.')
        .refine((v) => v === '' || PRACTICE_LOGO_RE.test(v), 'The logo must be a PNG, JPG or WebP image.'),
});
// ── Practice profile (Practice info tab) ────────────────────────────────────
/**
 * credify-platform#306 (E4): the business entity type, the V33 signup wizard's list. '' = not set.
 * The signup module writes it when it creates the practice; Practice info shows and edits it.
 */
export const PRACTICE_ENTITY_TYPES = [
    'Sole proprietor',
    'LLC',
    'Professional corporation',
    'S corporation',
    'C corporation',
    'Nonprofit',
    'Partnership',
];
/** credify-platform#306 (E4): the state of incorporation, a 2-letter code (50 states + DC) or ''. */
export const PRACTICE_INCORPORATION_STATES = PAYOR_STATES;
export const practiceProfileSchema = z.object({
    name: z.string().min(1).max(160),
    legalName: z.string().max(160).default(''),
    dba: z.string().max(160).default(''),
    practiceType: z.string().max(80).default(''),
    npi: z.string().max(20).default(''),
    taxId: z.string().max(20).default(''),
    /** credify-platform#306 (E4): one of PRACTICE_ENTITY_TYPES, or '' (not set). */
    entityType: z.union([z.literal(''), z.enum(PRACTICE_ENTITY_TYPES)]).default(''),
    /** credify-platform#306 (E4): 2-letter state of incorporation, or '' (not set). */
    incorporationState: z.union([z.literal(''), z.enum(PRACTICE_INCORPORATION_STATES)]).default(''),
    taxonomy: z.string().max(20).default(''),
    caqhId: z.string().max(20).default(''),
    phone: z.string().max(40).default(''),
    fax: z.string().max(40).default(''),
    email: z.string().max(200).default(''),
    website: z.string().max(200).default(''),
    timezone: z.string().max(80).default(''),
    /** ehr#98: the client-facing time format. Left out = keep the saved one. */
    clientTimeFormat: timeFormatSchema.optional(),
    billing: z
        .object({
        line1: z.string().max(200).default(''),
        line2: z.string().max(200).default(''),
        city: z.string().max(120).default(''),
        state: z.string().max(2).default(''),
        zip: z.string().max(12).default(''),
        sameAsPrimary: z.boolean().default(true),
    })
        .default({}),
});
//# sourceMappingURL=practice.js.map